import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { IUnregisteredStepOne } from '../unregistered-steps-types';
import { UnregisteredParams } from '../../unregistered-types';
import { hideModal } from '../../../../../store/slices/generalSlice';
import { RootState } from '../../../../../store/rootReducer';
import storageService from '../../../../../services/storage.service';
import { FormLayout } from '../../../../../global-styles/layout';
import { theme } from '../../../../../global-styles/colors';
import { getErrorMessage } from '../../../../../constants/errorMessages';
import { DUMMY_IDENTIFIER, INSURANCE_COMPANIES } from '../../../../../constants';
import { Heading4Text, BodySmallText } from '../../../../../components/typography/typography';
import ModalContainer from '../../../../../components/common/modal-container/modal-container';
import ControlledSelect from '../../../../../components/common/forms/ControlledSelect';
import ControlledInput from '../../../../../components/common/forms/ControlledInput';
import CardInfo from '../../../../../components/card-info/CardInfo';
import { UnregisteredStep1Style } from './unregistered-1-style';

const UnregisteredStep1: React.FC<IUnregisteredStepOne> = ({ form }) => {
  const { t } = useTranslation(['common', 'auth']);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const modal = useSelector((state: RootState) => state.general.modal);
  const params = useParams<UnregisteredParams>();

  const {
    control,
    formState: { errors },
    watch,
  } = form;

  const { data: showForm } = useQuery({
    queryKey: ['showForm'],
    queryFn: async () => {
      const localShowForm = await storageService.getItem<string>('showForm');
      return localShowForm === '1';
    },
    refetchOnWindowFocus: true,
    refetchInterval: (query) => (query.state.data ? false : 100),
  });

  const formValues = watch();

  return (
    showForm && (
      <>
        <UnregisteredStep1Style>
          <Heading4Text className="m-12px" weight={600}>
            {t('auth:unregistered.step-1.title')}
          </Heading4Text>
          <BodySmallText color={theme.type.secondary} className="m-24px">
            {t('auth:unregistered.step-1.text')}
          </BodySmallText>
          <FormLayout>
            <ControlledInput
              inputProps={{
                variant: 'filled',
                label: t('auth:unregistered.step-1.dni-label'),
                'data-cy': 'dni',
                error: !!errors?.dni,
                helperText: getErrorMessage(t, errors.dni?.type),
                formatOnChange: (e) => e.target.value.toUpperCase(),
                filter: 'alphanumeric',
              }}
              controllerProps={{
                control,
                name: 'dni',
                rules: {
                  required: true,
                },
              }}
            />
            <ControlledInput
              inputProps={{
                variant: 'filled',
                label: t('auth:unregistered.step-1.plate-label'),
                'data-cy': 'plate',
                error: !!errors?.plate,
                helperText: getErrorMessage(t, errors.plate?.type),
                placeholder: 'XXXXAAA',
                formatOnChange: (e) => e.target.value.toUpperCase(),
                filter: 'alphanumeric',
              }}
              controllerProps={{
                control,
                name: 'plate',
                rules: {
                  required: true,
                },
              }}
            />
            {!params.entityCode && (
              <ControlledSelect
                selectProps={{
                  variant: 'filled',
                  label: t('common:label-inputs.company'),
                  options: Object.values(INSURANCE_COMPANIES),
                  'data-cy': 'vehicle-company',
                  inputProps: {
                    error: !!errors?.insuranceCompany,
                  },
                  helpertext: getErrorMessage(t, errors.insuranceCompany?.type),
                }}
                controllerProps={{
                  control,
                  name: 'insuranceCompany',
                  rules: {
                    required: true,
                  },
                }}
              />
            )}
            {(!params.identifier || params.identifier === DUMMY_IDENTIFIER) && (
              <ControlledInput
                inputProps={{
                  variant: 'filled',
                  label: t('common:label-inputs.phone-contact-number'),
                  'data-cy': 'phone',
                  error: !!errors?.phone,
                  helperText: getErrorMessage(t, errors.phone?.type),
                  filter: 'phone',
                }}
                controllerProps={{
                  control,
                  name: 'phone',
                  rules: {
                    required: true,
                  },
                }}
              />
            )}
          </FormLayout>
        </UnregisteredStep1Style>

        {/* Modal de confirmación de datos introducidos: muestra el DNI y la matrícula
            para que el usuario verifique que son correctos antes de continuar */}
        <ModalContainer
          dataCy="confirm-data"
          buttons={[
            {
              className: 'secondary',
              onClick: () => {
                // El usuario decide editar los datos: se cierra el modal y se regresa al formulario
                dispatch(hideModal());
              },
              label: t('auth:unregistered.step-1.modal-confirm-edit'),
            },
          ]}
          active={modal.name === 'confirm-data'}
          onClose={() => {
            dispatch(hideModal());
          }}
          title={t('auth:unregistered.step-1.modal-confirm-title')}
        >
          <CardInfo title={formValues.dni} data={[formValues.plate]} />
        </ModalContainer>

        {/* Modal de error para el código 412: la póliza no permite autogestión.
            El usuario es redirigido al flujo de callback para ser atendido por un agente */}
        <ModalContainer
          buttons={[
            {
              className: 'secondary',
              onClick: () => {
                // Redirige al flujo de llamada de vuelta (callme-back) y cierra el modal
                navigate('/callme-back');
                dispatch(hideModal());
              },
              label: t('common:buttons.accept'),
            },
          ]}
          active={modal.name === 'error-confirm'}
          onClose={() => {}}
          title={t('auth:unregistered.step-1.modal-confirm-error-title')}
          text={t('auth:unregistered.step-1.modal-confirm-error-text')}
          required
        />

        {/* Modal de error genérico de verificación de póliza sin opción de callback.
            Se muestra cuando los datos no son válidos pero no aplica la redirección a callme-back */}
        <ModalContainer
          buttons={[
            {
              className: 'secondary',
              onClick: () => {
                // El usuario acepta el error y regresa al formulario para corregir los datos
                dispatch(hideModal());
              },
              label: t('common:buttons.accept'),
            },
          ]}
          active={modal.name === 'error-no-callmeback'}
          onClose={() => {}}
          title={t('auth:unregistered.step-1.modal-confirm-error-title')}
          text={t('auth:unregistered.step-1.modal-no-callmeback-error-text')}
          required
        />
      </>
    )
  );
};

export default UnregisteredStep1;
